import { useEffect, useMemo } from 'react';
import { useMachine } from '@xstate/react';
import { CommonError } from '../../../../stores/app-state-errors';
import {
  InteractionElement,
  ParentApplicationEvent,
  parentMessenger,
} from '../../../../core/Analytics/ParentApplicationEventMessenger';
import { useAnalytics } from '../../../../core/Analytics/AnalyticsProvider';
import { LoadingOverlay } from '../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { userFeedbackEnabled } from '../../../../models/configuration';
import { SessionModel } from '../../../../models/session';
import { applicationParameters } from '../../../../utilities/application-parameters';
import { CredentialsEntity } from '../../../../models/credentials';
import {
  createLoginMachine,
  LoginMachineContext,
} from '../../../../connectivity/machines/loginMachine';
import { ProviderBanner } from '../../SubmitCredentials/molecules/ProviderBanner/ProviderBanner';
import { Provider, ProviderEntity } from '../../../../models/provider';
import { LegalDisclaimer } from '../../../molecules/LegalDisclaimer/LegalDisclaimer';
import { ApplicationEvent, PageView, UserInteractionEvent } from '../../../../models/analytics';
import { useShouldShowUserHintId } from '../../SubmitCredentials/hooks/useShouldShowUserHintId';
import { Analytics } from '../../../../core/Analytics/tink-link-analytics';
import { MaybeNoFieldsScreen } from '../../../../connectivity/screens/login/MaybeNoFieldsScreen';
import { OauthDescribeSuccessResponse } from '../../../../lib-api/services/Oauth';
import { ExtendConsentView } from '../../../../extend-consent/components/ExtendConsentView';
import {
  SubmitCredentialsScreenView,
  SubmitCredentialsScreenViewProps,
} from './screens/LegacySubmitCredentialsScreenView/LegacySubmitCredentialsScreenView';

export const getExtendConsentTitles = (oauthDescribe: OauthDescribeSuccessResponse | undefined) =>
  (oauthDescribe?.scopesDescriptionsList.map((item) => item.title) as string[]) ?? [];

export const sendSubmitCredentialsEvents = (
  tinklinkAnalytics: Analytics,
  provider: ProviderEntity
) => {
  tinklinkAnalytics.sendInteractionEvent({
    action: `${PageView.SUBMIT_CREDENTIALS_SCREEN}/${UserInteractionEvent.SUBMIT_CREDENTIALS}`,
    view: PageView.SUBMIT_CREDENTIALS_SCREEN,
  });

  parentMessenger.fireInteractionEvent({
    pageView: PageView.SUBMIT_CREDENTIALS_SCREEN,
    element: InteractionElement.PRIMARY_BUTTON,
  });

  if (Provider.isOpenBanking(provider)) {
    parentMessenger.fireApplicationEvent(
      ParentApplicationEvent.PROVIDER_AUTHENTICATION_INITIALIZED
    );

    tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.PROVIDER_AUTHENTICATION_INITIALIZED);
  } else {
    parentMessenger.fireApplicationEvent(ParentApplicationEvent.CREDENTIALS_SUBMITTED);
    tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.CREDENTIALS_SUBMITTED);
  }
};

export type LoginMachineResponse =
  | {
      type:
        | 'CREDENTIALS_CREATED'
        | 'CREDENTIALS_UPDATED'
        | 'CREDENTIALS_REFRESHED'
        | 'EXISTING_CREDENTIALS_AUTHENTICATED'
        | 'EXISTING_CREDENTIALS_REFRESHED'
        | 'UPDATED_CONSENT'
        | 'EXTENDED_CONSENT';
      credentials: CredentialsEntity;
    }
  | {
      type: 'ERROR';
      error: CommonError;
    }
  | {
      type: 'BACK';
    };

export type LoginMachineViewProps = LoginMachineContext & {
  session: SessionModel | undefined;
  userHintId: string | undefined;
  renderProviderFields: boolean;
  oauthDescribe: OauthDescribeSuccessResponse | undefined;
  onDone: (r: LoginMachineResponse) => void;
  callbackUri?: string;
};

export const LoginMachineView = ({
  operation,
  session,
  userHintId,
  renderProviderFields,
  oauthDescribe,
  provider,
  productNames,
  configurationData,
  userId,
  selectedAuthenticationOptions,
  credentials,
  userCredentials,
  refreshableItems,
  credentialsId,
  onDone,
  callbackUri,
}: LoginMachineViewProps) => {
  const machine = useMemo(
    () =>
      createLoginMachine({
        context: {
          operation,
          provider,
          productNames,
          configurationData,
          userId,
          selectedAuthenticationOptions,
          credentials,
          userCredentials,
          refreshableItems,
          credentialsId,
        },
        callbackUri,
      }),
    [
      configurationData,
      credentials,
      credentialsId,
      operation,
      productNames,
      provider,
      refreshableItems,
      selectedAuthenticationOptions,
      userCredentials,
      userId,
      callbackUri,
    ]
  );
  const [current, send] = useMachine(machine);
  const { shouldShowUserHintId } = useShouldShowUserHintId();
  const { tinklinkAnalytics } = useAnalytics();

  useEffect(() => {
    if (current.matches('processCreateCredentialsDone'))
      onDone({
        type: 'CREDENTIALS_CREATED',
        credentials: current.context.credentials!,
      });

    if (current.matches('processRefreshCredentialsDone'))
      onDone({
        type: 'CREDENTIALS_REFRESHED',
        credentials: current.context.credentials!,
      });

    if (current.matches('processUpdateCredentialsDone'))
      onDone({
        type: 'CREDENTIALS_UPDATED',
        credentials: current.context.credentials!,
      });

    if (current.matches('processAuthenticateExistingCredentialsDone'))
      onDone({
        type: 'EXISTING_CREDENTIALS_AUTHENTICATED',
        credentials: current.context.credentials!,
      });

    if (current.matches('processRefreshExistingCredentialsDone'))
      onDone({
        type: 'EXISTING_CREDENTIALS_REFRESHED',
        credentials: current.context.credentials!,
      });

    if (current.matches('processUpdateConsentDone'))
      onDone({
        type: 'UPDATED_CONSENT',
        credentials: current.context.credentials!,
      });

    if (current.matches('processExtendConsentDone'))
      onDone({
        type: 'EXTENDED_CONSENT',
        credentials: current.context.credentials!,
      });

    if (current.matches('processError'))
      onDone({
        type: 'ERROR',
        error: current.context.error!,
      });

    if (current.matches('processBack'))
      onDone({
        type: 'BACK',
      });
  }, [current, onDone]);

  const username = applicationParameters.get('username');
  const personalIdentifier = session?.personalIdentifier;
  const providerFieldsVisible = renderProviderFields && Provider.hasFields(provider);

  const defaultProps: SubmitCredentialsScreenViewProps = {
    provider,
    userHintId,
    providerFieldsFallback: <ProviderBanner provider={provider} userHintId={userHintId} />,
    renderProviderFields,
    onBack: () => send('EVENT_BACK'),
    onSubmitCredentials: (fields) => {
      sendSubmitCredentialsEvents(tinklinkAnalytics, provider);
      send('EVENT_SUBMIT_CREDENTIALS', { fields });
    },
    legalDisclaimer: (
      <LegalDisclaimer provider={provider} pageView={PageView.SUBMIT_CREDENTIALS_SCREEN} />
    ),
    isCredentialsLoading: false,
    credentialsError: undefined,
    username,
    personalIdentifier,
    providerFieldsVisible,
    shouldShowUserHintId,
    isClientVerified: Boolean(oauthDescribe),
    isUserFeedbackEnabled: userFeedbackEnabled(configurationData),
  };

  if (
    current.matches('uiCreateOrRefreshCredentials') ||
    current.matches('uiAuthenticateExistingCredentials') ||
    current.matches('uiRefreshExistingCredentials') ||
    current.matches('uiUpdateConsent')
  ) {
    return (
      <MaybeNoFieldsScreen
        provider={defaultProps.provider}
        onBack={defaultProps.onBack}
        onSubmit={({ fields }) => defaultProps.onSubmitCredentials(fields)}
      >
        <SubmitCredentialsScreenView {...defaultProps} />
      </MaybeNoFieldsScreen>
    );
  }

  if (current.matches('uiExtendConsent')) {
    return (
      <ExtendConsentView
        titles={getExtendConsentTitles(oauthDescribe)}
        providerName={provider.displayName}
        isLoading={false}
        onClick={() => {
          sendSubmitCredentialsEvents(tinklinkAnalytics, provider);
          send('EVENT_SUBMIT_CREDENTIALS');
        }}
      />
    );
  }

  if (current.matches('uiUpdateCredentials')) {
    return (
      <MaybeNoFieldsScreen
        provider={defaultProps.provider}
        onBack={defaultProps.onBack}
        onSubmit={({ fields }) => defaultProps.onSubmitCredentials(fields)}
      >
        <SubmitCredentialsScreenView
          {...defaultProps}
          credentialsError={current.context.credentials?.statusPayload}
        />
      </MaybeNoFieldsScreen>
    );
  }

  if (
    current.matches('uiPollingCreateCredentials') ||
    current.matches('uiPollingRefreshCredentials') ||
    current.matches('uiPollingUpdateCredentials')
  ) {
    return (
      <MaybeNoFieldsScreen
        provider={defaultProps.provider}
        onBack={defaultProps.onBack}
        onSubmit={({ fields }) => defaultProps.onSubmitCredentials(fields)}
      >
        <SubmitCredentialsScreenView {...defaultProps} isCredentialsLoading />
      </MaybeNoFieldsScreen>
    );
  }

  return <LoadingOverlay />;
};
